"use server";
// РУКА 2. Поиск по названию и артикулу.
// В договоре между руками пока нет функции поиска, поэтому берём все товары по разделам (ruka3.listProducts)
// и ищем здесь. Для 220 тестовых товаров этого хватает; для каталога из 1С нужна функция поиска на стороне
// базы — это изменение договора (src/contract), решают втроём.
//
// Что прощаем покупателю: другую форму слова («тёплый пол» → «тёплого пола»), опечатку в одну-две буквы
// («радиотор»), латинскую букву внутри русского слова («насоc»), забытую раскладку («yfcjc» → «насос»,
// как в макете). Артикул — без дефиса и в любом регистре: «sm17» найдёт «SM-173137».
import { ruka3 } from "@/ruka3";
import type { ProductView } from "@/contract";
import { salePrice } from "./pricing";

export type Suggestion = { id: string; name: string; code: string; price: number; inStock: boolean };

const EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,.`";
const RU = "йцукенгшщзхъфывапролджэячсмитьбюё";
const LOOKALIKE: Record<string, string> = { a: "а", c: "с", e: "е", o: "о", p: "р", x: "х", y: "у", k: "к", m: "м", t: "т", h: "н", b: "в" };

function norm(s: string): string {
  return s.toLowerCase().replace(/ё/g, "е").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}
// Латинские буквы-двойники внутри русского слова → русские («насоc» → «насос»).
function fixMixed(s: string): string {
  return s.replace(/\S+/g, (w) => (/[а-яё]/i.test(w) && /[a-z]/i.test(w) ? w.replace(/[a-z]/gi, (ch) => LOOKALIKE[ch.toLowerCase()] ?? ch) : w));
}
// Набрано в английской раскладке → русские буквы с тех же клавиш.
function fromLayout(s: string): string {
  return [...s.toLowerCase()].map((ch) => {
    const i = EN.indexOf(ch);
    return i >= 0 ? RU[i] : ch;
  }).join("");
}
// Основа русского слова без окончания: «тёплый» → «тепл», «радиатор» → «радиат».
function stem(w: string): string {
  return /[а-я]/.test(w) && !/\d/.test(w) && w.length >= 5 ? w.slice(0, Math.max(4, w.length - 2)) : w;
}
// Сколько правок отделяет одно слово от другого: лишняя, пропущенная, неверная буква
// или две соседние буквы местами («турба» → «труба») — каждая считается за одну.
function lev(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[a.length][b.length];
}
// 0 — слово нашлось как есть, 1 — нашлось с опечаткой, -1 — не нашлось.
// Опечатки прощаем словам от minLen букв (обычно от 5; если точно не нашлось ничего — от 4).
function hit(w: string, hay: string, hayWords: string[], minLen = 5): number {
  if (hay.includes(stem(w))) return 0;
  if (w.length < minLen || /\d/.test(w)) return -1;
  const max = w.length >= 8 ? 2 : 1;
  const lens = w.length <= 4 ? [w.length] : [w.length - 1, w.length, w.length + 1];
  for (const hw of hayWords) {
    for (const len of lens) {
      if (len <= hw.length && lev(w, hw.slice(0, len)) <= max) return 1;
    }
  }
  return -1;
}

async function allProducts(): Promise<ProductView[]> {
  const cats = await ruka3.listCategories();
  const lists = await Promise.all(cats.map((c) => ruka3.listProducts(c.id)));
  return lists.flat();
}

function rank(products: ProductView[], q: string, minLen = 5): [number, ProductView][] {
  if (q.length < 2) return [];
  const words = q.split(" ");
  const qCompact = q.replace(/ /g, "");
  const out: [number, ProductView][] = [];
  for (const p of products) {
    const code = norm(p.code);
    const codeCompact = code.replace(/ /g, "");
    const name = norm(p.name);
    const hay = `${name} ${code} ${codeCompact} ${norm(p.brand ?? "")}`;
    const hayWords = hay.split(" ");
    let fuzzy = 0;
    let ok = true;
    for (const w of words) {
      const h = hit(w, hay, hayWords, minLen);
      if (h < 0) {
        ok = false;
        break;
      }
      fuzzy += h;
    }
    if (!ok) continue;
    const first = name.split(" ")[0];
    const base = codeCompact.startsWith(qCompact) ? 0 : name.startsWith(q) ? 1 : hit(words[0], first, [first], minLen) >= 0 ? 2 : 3;
    out.push([base + 10 * fuzzy, p]);
  }
  return out;
}

export async function findProducts(query: string, limit = 60): Promise<ProductView[]> {
  const products = await allProducts();
  const q = norm(fixMixed(query));
  let scored = rank(products, q);
  if (scored.length === 0 && /[a-z\[\];',.`]/i.test(query)) scored = rank(products, norm(fromLayout(query)));
  if (scored.length === 0) scored = rank(products, q, 4);
  scored.sort((a, b) => a[0] - b[0] || a[1].name.localeCompare(b[1].name, "ru"));
  return scored.slice(0, limit).map(([, p]) => p);
}

export async function suggest(query: string): Promise<Suggestion[]> {
  const found = await findProducts(query, 6);
  // цена — та же, что в плитке и карточке: со скидкой магазина (salePrice)
  return found.map((p) => ({ id: p.id, name: p.name, code: p.code, price: salePrice(p), inStock: p.inStock }));
}
